/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useRef, useEffect, useMemo, useState } from '@web-stories-wp/react';
import { getMediaSizePositionProps } from '@web-stories-wp/media';
/**
 * Internal dependencies
 */
import StoryPropTypes from '../../types';
import { useConfig } from '../../app/config';
import MediaDisplay from '../media/display';
import { getBackgroundStyle, videoWithScale } from './util';
import {
  getDynamicCropMediaSizePositionProps,
  normalizeDynamicCropPositions,
} from './dynamicCrop';
import { useDynamicCropRecorderState } from './dynamicCropRecorder';
import LoadingIndicator from '../../components/loading-indicator/LoadingIndicator';

const PLAYBACK_FRAME_TOLERANCE_SECONDS = 1 / 60;

const Video = styled.video`
  position: absolute;
  max-width: initial;
  max-height: initial;
  ${videoWithScale}
`;

const Image = styled.img`
  position: absolute;
  max-height: initial;
  object-fit: contain;
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  left: ${({ offsetX }) => `${-offsetX}px`};
  top: ${({ offsetY }) => `${-offsetY}px`};
  max-width: ${({ isBackground }) => (isBackground ? 'initial' : null)};
`;

const CenteredLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
`;

const LoadingMask = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.5);
`;

function VideoDisplay({ previewMode, box: { width, height }, element }) {
  const {
    id,
    poster,
    resource,
    tracks,
    isBackground,
    scale,
    focalX,
    focalY,
    loop,
    dynamicCrop,
  } = element;
  const ref = useRef();
  const animatedThumbnail = useConfig(
    ({ animatedThumbnail: config }) => config
  );
  const dynamicCropRecorderState = useDynamicCropRecorderState();
  const wasDynamicCropRecorderActiveRef = useRef(false);
  const [dynamicCropCurrentTime, setDynamicCropCurrentTime] = useState(0);
  const hasDynamicCrop =
    isBackground && Boolean(dynamicCrop?.positions?.length);
  const isDynamicCropRecorderActive = dynamicCropRecorderState.elementId === id;
  const playbackStartSeconds = animatedThumbnail?.playbackStartSeconds;
  const playbackDurationSeconds = animatedThumbnail?.fixedDurationSeconds;
  const isAnimatedThumbnailSource =
    Boolean(animatedThumbnail?.sourceUrl) &&
    resource?.src === animatedThumbnail.sourceUrl;
  const hasBoundedPlayback = Boolean(
    isAnimatedThumbnailSource &&
      Number.isFinite(playbackStartSeconds) &&
      playbackStartSeconds >= 0 &&
      Number.isFinite(playbackDurationSeconds) &&
      playbackDurationSeconds > 0
  );
  const dynamicCropPositions = useMemo(
    () => (hasDynamicCrop ? normalizeDynamicCropPositions(dynamicCrop) : []),
    [dynamicCrop, hasDynamicCrop]
  );

  useEffect(() => {
    if (isDynamicCropRecorderActive) {
      wasDynamicCropRecorderActiveRef.current = true;
      try {
        ref.current?.pause();
      } catch {
        // Some test media elements do not implement pause; browsers should not throw.
      }
      return;
    }

    if (!wasDynamicCropRecorderActiveRef.current) {
      return;
    }

    wasDynamicCropRecorderActiveRef.current = false;
    setDynamicCropCurrentTime(0);
    try {
      const node = ref.current;
      node?.pause();
      if (node) {
        node.currentTime = 0;
      }
    } catch {
      // Some media elements do not allow seeking before metadata is ready.
    }
  }, [isDynamicCropRecorderActive]);

  useEffect(() => {
    if (!hasBoundedPlayback || previewMode || isDynamicCropRecorderActive) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    let frameId;
    const getPlaybackBounds = () => {
      const sourceDuration =
        Number.isFinite(node.duration) && node.duration > 0
          ? node.duration
          : Infinity;
      const start = Math.min(
        playbackStartSeconds,
        Math.max(0, sourceDuration - playbackDurationSeconds)
      );
      return {
        start,
        end: Math.min(start + playbackDurationSeconds, sourceDuration),
      };
    };
    const stopMonitoring = () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = undefined;
    };
    const seekToStart = () => {
      const { start } = getPlaybackBounds();
      try {
        node.currentTime = start;
      } catch {
        // Some browsers reject seeking until video metadata is available.
      }
    };
    const enforcePlaybackBounds = () => {
      const { start, end } = getPlaybackBounds();
      if (
        node.currentTime < start ||
        node.currentTime >= end - PLAYBACK_FRAME_TOLERANCE_SECONDS
      ) {
        seekToStart();
      }
    };
    const monitorPlayback = () => {
      frameId = undefined;
      enforcePlaybackBounds();
      if (!node.paused && !node.ended) {
        frameId = window.requestAnimationFrame(monitorPlayback);
      }
    };
    const startMonitoring = () => {
      stopMonitoring();
      enforcePlaybackBounds();
      if (!node.paused && !node.ended) {
        frameId = window.requestAnimationFrame(monitorPlayback);
      }
    };
    const continueFromStart = () => {
      seekToStart();
      node.play()?.catch(() => {});
    };

    node.addEventListener('loadedmetadata', seekToStart);
    node.addEventListener('play', startMonitoring);
    node.addEventListener('pause', stopMonitoring);
    node.addEventListener('timeupdate', enforcePlaybackBounds);
    node.addEventListener('ended', continueFromStart);

    if (node.readyState >= 1) {
      seekToStart();
    }
    if (!node.paused && !node.ended) {
      startMonitoring();
    }

    return () => {
      stopMonitoring();
      node.removeEventListener('loadedmetadata', seekToStart);
      node.removeEventListener('play', startMonitoring);
      node.removeEventListener('pause', stopMonitoring);
      node.removeEventListener('timeupdate', enforcePlaybackBounds);
      node.removeEventListener('ended', continueFromStart);
    };
  }, [
    hasBoundedPlayback,
    isDynamicCropRecorderActive,
    playbackDurationSeconds,
    playbackStartSeconds,
    previewMode,
    resource?.src,
  ]);

  useEffect(() => {
    if (!hasDynamicCrop || previewMode || isDynamicCropRecorderActive) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    let frameId;
    const updateCurrentTime = () => {
      setDynamicCropCurrentTime(node.currentTime || 0);
    };
    const stopAnimation = () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = undefined;
    };
    const tick = () => {
      updateCurrentTime();
      if (!node.paused && !node.ended) {
        frameId = window.requestAnimationFrame(tick);
      }
    };
    const startAnimation = () => {
      stopAnimation();
      tick();
    };
    const stopAndSync = () => {
      stopAnimation();
      updateCurrentTime();
    };

    node.addEventListener('loadedmetadata', updateCurrentTime);
    node.addEventListener('seeked', updateCurrentTime);
    node.addEventListener('timeupdate', updateCurrentTime);
    node.addEventListener('play', startAnimation);
    node.addEventListener('pause', stopAndSync);
    node.addEventListener('ended', stopAndSync);

    updateCurrentTime();
    if (!node.paused && !node.ended) {
      startAnimation();
    }

    return () => {
      stopAnimation();
      node.removeEventListener('loadedmetadata', updateCurrentTime);
      node.removeEventListener('seeked', updateCurrentTime);
      node.removeEventListener('timeupdate', updateCurrentTime);
      node.removeEventListener('play', startAnimation);
      node.removeEventListener('pause', stopAndSync);
      node.removeEventListener('ended', stopAndSync);
    };
  }, [hasDynamicCrop, isDynamicCropRecorderActive, previewMode, resource?.src]);

  const dynamicVideoProps = useMemo(
    () =>
      hasDynamicCrop
        ? getDynamicCropMediaSizePositionProps(
            resource,
            width,
            height,
            dynamicCrop,
            dynamicCropCurrentTime,
            dynamicCropPositions
          )
        : null,
    [
      dynamicCrop,
      dynamicCropCurrentTime,
      dynamicCropPositions,
      hasDynamicCrop,
      height,
      resource,
      width,
    ]
  );
  const videoProps =
    dynamicVideoProps ||
    getMediaSizePositionProps(resource, width, height, scale, focalX, focalY);

  videoProps.crossOrigin = 'anonymous';

  const style = isBackground && !dynamicVideoProps ? getBackgroundStyle() : {};

  const loading = Boolean(resource?.loading);
  const videoSourceKey = `${resource?.mimeType || ''}:${resource?.src || ''}`;

  return (
    <MediaDisplay
      element={element}
      mediaRef={ref}
      showPlaceholder
      previewMode={previewMode}
    >
      {previewMode ? (
        (poster || resource.poster) && (
          <Image
            src={poster || resource.poster}
            alt={element.alt || resource.alt}
            style={style}
            {...videoProps}
            ref={ref}
            draggable={false}
          />
        )
      ) : (
        // eslint-disable-next-line styled-components-a11y/media-has-caption,jsx-a11y/media-has-caption -- False positive.
        <Video
          key={videoSourceKey}
          id={`video-${id}`}
          poster={poster || resource.poster}
          style={style}
          {...videoProps}
          preload={hasBoundedPlayback ? 'auto' : 'metadata'}
          muted={hasBoundedPlayback}
          autoPlay={hasBoundedPlayback}
          loop={hasBoundedPlayback ? false : loop}
          ref={ref}
          data-testid="videoElement"
          data-leaf-element="true"
        >
          {resource.src && (
            <>
              <source src={resource.src} type={resource.mimeType} />
              {/* Trick to get .mov files to play in Chrome */}
              <source src={resource.src} />
            </>
          )}
          {tracks &&
            tracks.map(({ srclang, label, kind, track: src, id: key }, i) => (
              <track
                srcLang={srclang}
                label={label}
                kind={kind}
                src={src}
                key={key}
                default={i === 0}
              />
            ))}
        </Video>
      )}
      {!previewMode && loading && (
        <>
          <LoadingMask />
          <CenteredLoadingIndicator />
        </>
      )}
    </MediaDisplay>
  );
}

VideoDisplay.propTypes = {
  previewMode: PropTypes.bool,
  element: StoryPropTypes.elements.video.isRequired,
  box: StoryPropTypes.box.isRequired,
};

export default VideoDisplay;
